import { createTheme } from '@mui/material/styles';

// ============================================================================
// Nađi Investitora — shared design system (dark + gold), matched to the iOS app
// and the landing page. Single source of truth for the whole web client.
// ============================================================================

export const C = {
  bg: '#0A0A0B', // page background
  surface: '#141416', // elevated cards / sections
  surface2: '#1E1E21', // inner surfaces / inputs
  line: 'rgba(255,255,255,0.09)',
  lineStrong: 'rgba(255,255,255,0.16)',
  gold: '#F3C015', // brand accent (links, icons, borders)
  goldSoft: 'rgba(243,192,21,0.12)',
  goldGrad: 'linear-gradient(95deg, #F7CE2A 0%, #E0A21F 55%, #CE851F 100%)',
  onGold: '#1A1400', // text on gold surfaces
  white: '#FFFFFF',
  muted: '#9C9CA3', // secondary text
  danger: '#FF5A5A',
  success: '#3FBF7F',
};

export const DISPLAY = '"Inter", "Helvetica", "Arial", sans-serif';
export const reduceMotion = '@media (prefers-reduced-motion: reduce)';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: C.gold, contrastText: C.onGold },
    secondary: { main: C.white, contrastText: C.bg },
    background: { default: C.bg, paper: C.surface },
    text: { primary: C.white, secondary: C.muted },
    divider: C.line,
    error: { main: C.danger },
    success: { main: C.success },
    warning: { main: '#F3C015' },
    info: { main: C.gold },
  },
  typography: {
    fontFamily: DISPLAY,
    h1: { fontWeight: 800, letterSpacing: '-1px', lineHeight: 1.15 },
    h2: { fontWeight: 800, letterSpacing: '-0.5px', lineHeight: 1.2 },
    h3: { fontWeight: 700, letterSpacing: '-0.4px', lineHeight: 1.25 },
    h4: { fontWeight: 700, letterSpacing: '-0.3px', lineHeight: 1.3 },
    h5: { fontWeight: 700, lineHeight: 1.35 },
    h6: { fontWeight: 600, lineHeight: 1.4 },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.6 },
    button: { fontWeight: 700, textTransform: 'none', letterSpacing: '0.2px' },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: C.bg, color: C.white },
        '::selection': { background: 'rgba(243,192,21,0.30)' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 700,
          padding: '10px 22px',
          fontSize: '1rem',
          transition: 'all 0.25s cubic-bezier(0.16,1,0.3,1)',
          [reduceMotion]: { transition: 'none' },
        },
        contained: {
          background: C.goldGrad,
          color: C.onGold,
          boxShadow: '0 8px 26px rgba(224,162,31,0.30)',
          '&:hover': {
            filter: 'brightness(1.06)',
            boxShadow: '0 10px 32px rgba(224,162,31,0.45)',
          },
          '&.Mui-disabled': { background: C.surface2, color: C.muted },
        },
        outlined: {
          color: '#F6CB4B',
          borderColor: 'rgba(243,192,21,0.55)',
          borderWidth: '1.5px',
          '&:hover': {
            borderColor: C.gold,
            backgroundColor: C.goldSoft,
            borderWidth: '1.5px',
          },
        },
        text: {
          color: '#F6CB4B',
          '&:hover': { backgroundColor: C.goldSoft },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: C.surface,
          borderRadius: 14,
        },
        outlined: { border: `1px solid ${C.line}` },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: C.surface,
          borderRadius: 16,
          border: `1px solid ${C.line}`,
          boxShadow: 'none',
          transition: 'all 0.25s cubic-bezier(0.16,1,0.3,1)',
          [reduceMotion]: { transition: 'none' },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: C.surface2,
          borderRadius: 10,
          color: C.white,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: C.line },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.lineStrong },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: C.gold,
            borderWidth: '1.5px',
          },
        },
        input: { '&::placeholder': { color: C.muted, opacity: 1 } },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: C.muted, '&.Mui-focused': { color: C.gold } },
      },
    },
    MuiFormLabel: {
      styleOverrides: { root: { '&.Mui-focused': { color: C.gold } } },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontWeight: 600, backgroundColor: C.surface2 },
        outlined: { borderColor: C.line },
      },
    },
    MuiLink: {
      styleOverrides: { root: { color: '#F6CB4B', textDecorationColor: 'rgba(243,192,21,0.4)' } },
    },
    MuiAppBar: {
      styleOverrides: { root: { boxShadow: 'none', backgroundImage: 'none' } },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: C.line } },
    },
    MuiContainer: {
      styleOverrides: { root: { paddingLeft: 24, paddingRight: 24 } },
    },
    MuiMenu: {
      styleOverrides: { paper: { backgroundColor: C.surface, border: `1px solid ${C.line}` } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: C.surface2, border: `1px solid ${C.line}`, fontSize: '0.78rem' },
      },
    },
  },
});

export default theme;
